import { X } from "lucide-react";
import React from "react";
import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "react-toastify";

export default function HireTalent({ handlerhire, value }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyname, setCompanyName] = useState("");
  const [positiontofill, setPositiontofill] = useState("");
  const [jobrequirement, setJobRequirement] = useState("");
  const [resume, setResume] = useState(null);
  const [coverletter, setCoverletter] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Handle file selection for resume
  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      // Check file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload only PDF, DOC, or DOCX files");
        return;
      }

      setResume(file);
    }
  };

  // Handle file selection for cover letter
  const handleCoverLetterChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      // Check file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload only PDF, DOC, or DOCX files");
        return;
      }

      setCoverletter(file);
    }
  };

  const handlerform = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (value === "hiretalent") {
      // Handle hire talent form
      const serviceid = "service_p3ai1hn";
      const templateid = "template_2arl08b";
      const publickey = "3HauRSnuioqlkt8O6";
      const data = {
        from_type: 'Hire a talent',
        name: name,
        from_email: email,
        from_company: companyname,
        position: positiontofill,
        from_phone: phone,
        from_requirement: jobrequirement,
      };

      try {
        await emailjs.send(serviceid, templateid, data, publickey);
        toast.success("Email sent successfully!");
        // Reset form
        setName("");
        setEmail("");
        setPhone("");
        setCompanyName("");
        setPositiontofill("");
        setJobRequirement("");
      } catch (error) {
        toast.error("Error sending email");
        console.error("EmailJS Error:", error);
      }
    } else {
      // Handle job application form
      const serviceid = "service_p3ai1hn";
      const templateid = "template_3z563bs";
      const publickey = "3HauRSnuioqlkt8O6";

      setUploadProgress(10);

      try {
        setUploadProgress(50);

        // Create email content for employer
        let emailContent = `
🔔 NEW JOB APPLICATION RECEIVED

📋 Applicant Details:
- Name: ${name}
- Email: ${email}
- Phone: ${phone}

📁 Files Submitted:`;

        if (resume) {
          emailContent += `
📄 Resume: ${resume.name} (${(resume.size / 1024 / 1024).toFixed(2)} MB)`;
        }

        if (coverletter) {
          emailContent += `
📝 Cover Letter: ${coverletter.name} (${(
            coverletter.size /
            1024 /
            1024
          ).toFixed(2)} MB)`;
        }

        if (!resume && !coverletter) {
          emailContent += `
📄 No files attached`;
        }

        emailContent += `

📧 NEXT STEPS:
1. Expect an email from ${email} with file attachments
2. Files will be sent to: Application@rtventure.net
3. Review attached documents
4. Contact ${name} for interview scheduling

📞 Contact: ${email} | ${phone}

⚠️ The applicant has been instructed to email files directly to ensure delivery.`;

        const data = {
          from_type: 'Find a job',
          name: name,
          from_email: email,
          from_phone: phone,
          application_details: emailContent,
          resume_provided: resume ? "yes" : "no",
          cover_letter_provided: coverletter ? "yes" : "no",
        };

        setUploadProgress(90);
        toast.info("📧 Sending notification email...");

        await emailjs.send(serviceid, templateid, data, publickey);

        // Send instruction email to applicant if they have files
        if (resume || coverletter) {
          setUploadProgress(95);
          toast.info("📤 Sending instructions to applicant...");

          const filestoSend = [];
          if (resume) filestoSend.push(resume.name);
          if (coverletter) filestoSend.push(coverletter.name);

          const applicantEmailData = {
            to_name: name,
            to_email: email,
            company_email: "Application@rtventure.net",
            files_to_send: filestoSend.join(", "),
          };

          // Send email to applicant with instructions
          try {
            await emailjs.send(
              "service_p3ai1hn",
              "template_applicant_instructions",
              applicantEmailData,
              "3HauRSnuioqlkt8O6"
            );
          } catch (error) {
            console.log(
              "Applicant instruction email failed, but main application was successful"
            );
          }
        }

        setUploadProgress(100);
        toast.success(
          "🎉 Application submitted successfully! We'll contact you soon."
        );

        // Reset form
        setName("");
        setEmail("");
        setPhone("");
        setResume(null);
        setCoverletter(null);
        setUploadProgress(0);

        // Reset file inputs
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach((input) => (input.value = ""));
      } catch (error) {
        setUploadProgress(0);
        toast.error(`❌ Error: ${error.message}`);
        console.error("Application submission error:", error);
      }
    }

    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 backdrop-blur-md">
      <div className="w-full min-h-[100vh] flex justify-center items-center overflow-y-auto py-10">
        <div className="w-[70%] max-h-[90vh] p-8 bg-white flex flex-col items-center relative rounded-md overflow-y-auto">
          {value === "hiretalent" ? (
            <h1 className="text-2xl font-bold font-sans text-black">
              Hire Talent
            </h1>
          ) : (
            <h1 className="text-2xl py-5 font-bold font-sans text-black">
              Find a Job
            </h1>
          )}
          <div className="absolute top-1 right-1 ">
            <X
              size={30}
              className="cursor-pointer text-gray-600 hover:text-black"
              onClick={handlerhire}
            />
          </div>

          <div className="w-full space-y-5 ">
            <form action="" onSubmit={handlerform} className="space-y-5 z-30">
              {/* Hire talent form */}
              {value === "hiretalent" ? (
                <div className="space-y-5 z-0">
                  <div className="grid sm:grid-cols-2 gap-5 z-30 ">
                    <div className="space-y-1">
                      <label
                        htmlFor=""
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Your Name
                      </label>
                      <input
                        value={name}
                        required
                        type="text"
                        className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        htmlFor=""
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Email Address
                      </label>
                      <input
                        value={email}
                        required
                        type="email"
                        className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1">
                      <label
                        htmlFor=""
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Phone Number
                      </label>
                      <input
                        value={phone}
                        required
                        type="text"
                        className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        htmlFor=""
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Company Name
                      </label>
                      <input
                        value={companyname}
                        required
                        type="text"
                        className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                        onChange={(e) => setCompanyName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className=" ">
                    <label
                      htmlFor=""
                      className="text-md font-semibold font-sans text-gray-700"
                    >
                      Position to fill
                    </label>
                    <input
                      value={positiontofill}
                      required
                      type="text"
                      className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                      onChange={(e) => setPositiontofill(e.target.value)}
                    />
                  </div>
                  <div className=" ">
                    <label
                      htmlFor=""
                      className="text-md font-semibold font-sans text-gray-700"
                    >
                      Job Requirement
                    </label>
                    <textarea
                      value={jobrequirement}
                      required
                      rows={5}
                      type="text"
                      className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                      onChange={(e) => setJobRequirement(e.target.value)}
                    />
                  </div>
                  <div className="bg-blue-600 hover:bg-blue-700 flex items-center justify-center py-3 text-white text-lg rounded-md transition-colors duration-300">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Sending...
                        </>
                      ) : (
                        "Submit Hiring Request"
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // Find a job form
                <div className="space-y-5 z-10">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800">
                      <strong>📧 Simple & Reliable Process:</strong> After
                      submitting this form, you'll receive an email asking you
                      to send your resume and cover letter directly to our
                      recruitment team at{" "}
                      <strong>Application@rtventure.net</strong>
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      ✅ This ensures we receive your files in perfect quality
                      with no upload issues!
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1">
                      <label
                        htmlFor=""
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Your Name
                      </label>
                      <input
                        value={name}
                        required
                        type="text"
                        className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label
                        htmlFor=""
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Email Address
                      </label>
                      <input
                        value={email}
                        required
                        type="email"
                        className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className=" ">
                    <label
                      htmlFor=""
                      className="text-md font-semibold font-sans text-gray-700"
                    >
                      Phone Number
                    </label>
                    <input
                      value={phone}
                      required
                      type="text"
                      className="w-full outline-none border border-gray-300 py-1 rounded-md px-3 text-lg"
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1">
                      <label
                        htmlFor="resume"
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Resume (PDF, DOC, DOCX) *
                      </label>
                      <input
                        id="resume"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="w-full outline-none border border-gray-300 py-2 px-3 rounded-md text-lg file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        onChange={handleResumeChange}
                        required
                      />
                      <div className="text-sm text-gray-600">
                        {resume ? (
                          <span className="text-green-600">
                            ✓ {resume.name} (
                            {(resume.size / 1024 / 1024).toFixed(2)} MB) - Will
                            be requested via email
                          </span>
                        ) : (
                          <span>
                            Max size: 5MB. You'll email this file after
                            submitting the form.
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label
                        htmlFor="coverletter"
                        className="text-md font-semibold font-sans text-gray-700"
                      >
                        Cover Letter (Optional)
                      </label>
                      <input
                        id="coverletter"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="w-full outline-none border border-gray-300 py-2 px-3 rounded-md text-lg file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        onChange={handleCoverLetterChange}
                      />
                      <div className="text-sm text-gray-600">
                        {coverletter ? (
                          <span className="text-green-600">
                            ✓ {coverletter.name} (
                            {(coverletter.size / 1024 / 1024).toFixed(2)} MB) -
                            Will be requested via email
                          </span>
                        ) : (
                          <span>
                            Max size: 5MB. You'll email this file after
                            submitting the form.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-600 hover:bg-blue-700 flex items-center justify-center py-3 text-white text-lg rounded-md transition-colors duration-300">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2 w-full justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          {uploadProgress > 0 ? (
                            <span>
                              {uploadProgress < 30
                                ? "Processing Application..."
                                : uploadProgress < 60
                                ? "Sending Notification..."
                                : uploadProgress < 90
                                ? "Sending Instructions..."
                                : "Finishing..."}{" "}
                              ({uploadProgress}%)
                            </span>
                          ) : (
                            "Submitting..."
                          )}
                        </>
                      ) : (
                        <>📄 Submit Application</>
                      )}
                    </button>
                  </div>

                  {/* Progress Bar */}
                  {isSubmitting && uploadProgress > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
